// Warframe.com global scripting
// Global navigation, login, helpers

import {
    elementOrParentAlreadyHasListener,
    elementParentAnchor,
    detectAnalyticsPlacement,
    addDataLayerListener,
    addDataLayerTrigger,
    autowireAnalytics,
    openAnalyticsDebug,
} from './includes/_analytics';
import DOMPurify from 'dompurify';
import {getUserData} from './includes/_userdata';
import {flashMessage} from './includes/_flashmessage';
import PageComponents from './components';

const KEY_SESSIONSTORAGE_NOTIFICATION_READ_PRIME = "wfPrimeNotificationRead";
const KEY_SESSIONSTORAGE_NOTIFICATION_READ_COUPON = "wfCouponNotificationRead";
const tabletMatchList = window.matchMedia("(min-width: 768px)");
const desktopMatchList = window.matchMedia("(min-width: 1280px)");
const wideMatchList = window.matchMedia("(min-width: 1600px)");

// adding `window.Warframe` singleton to catch anything that gets registered to `window.Warframe` before this runs
window.Warframe = window.Warframe || {};

window.createFlashMessage = (msg, status = 'info', className= "flash-message") => {
    //error / danger, success, info for js errors
    if(status === 'error') {
        status = 'danger';
    }
    let flashClass = 'alert-' + status;

    const PageWrapper = document.querySelector(".wrapper");
    if (PageWrapper) {
        const ChildElement = PageWrapper.querySelector('.' + flashClass);
        if (!ChildElement) {
            const AlertElement = document.createElement('div');
            AlertElement.classList.add('alert', flashClass, className);
            const AlertLink = document.createElement('a');
            AlertLink.classList.add('close');
            AlertLink.href = '#';
            AlertLink.setAttribute('data-dismiss', 'alert');
            AlertLink.setAttribute('aria-hidden', 'true');
            AlertLink.addEventListener('click', (e) => {
                document.activeElement && document.activeElement.blur();
                AlertElement.classList.add('dismissed');
            });
            AlertLink.innerHTML = "&times;";
            const AlertText = document.createElement('span');
            const ul = document.createElement('ul');

            if(Array.isArray(msg)) {
                msg.forEach(function (sentence) {
                    const li = document.createElement('li');
                    li.innerHTML = DOMPurify.sanitize(sentence, {
                        ALLOWED_TAGS: ['a'],
                        ALLOWED_ATTR: ['href'],
                    });
                    ul.appendChild(li);
                });

                AlertElement.appendChild(ul);
            } else {
                AlertText.innerHTML = DOMPurify.sanitize(msg, {
                    ALLOWED_TAGS: ['a'],
                    ALLOWED_ATTR: ['href'],
                });
                AlertElement.appendChild(AlertText);
            }

            AlertElement.appendChild(AlertLink);
            PageWrapper.appendChild(AlertElement);
        }
    }
    else
    {
        console.error('Could not attach flash message due to missing page wrapper: ' + msg);
    }
    /*
    if(!$('.wrapper').children('.'+flashClass).length) {
        $('.wrapper').append(
            '<div class="alert '+flashClass+' '+className+'">' +
            '<a href="#" class="close" data-dismiss="alert" aria-hidden="true" onclick="$(\'.wrapper .'+className+'\').fadeOut(300, function() { $(this).remove(); })">×</a>\n' +
            msg +
            '</div>');
    }
    */
}

export function isHeaderActive() {
    return !!document.querySelector(".HeaderNavigationBar");
}

export function isInVerticalView(element, viewport=null) {
    const rect = element.getBoundingClientRect();
    if (viewport) {
        const r = element.getBoundingClientRect();
        let {top, bottom} = r;

        return (bottom > rect.top && top < rect.bottom);
    } else {
        return (rect.bottom > 0 && rect.top < window.innerHeight);
    }
}

function lockPageScrolling () {
    document.body.classList.add('scroll-disabled');
}
function unlockPageScrolling () {
    document.body.classList.remove('scroll-disabled');
}

let isPrimeNotificationActive = false;
let isCouponNotificationActive = false;
export function setUserData(cookieData) {
    // Stop if header is not present
    if (!Warframe.isHeaderActive()) return;

    // Inject player name and mastery rank
    document.querySelector('.HeaderUserProfile-nameText').innerText = cookieData.display_name;
    let rank = cookieData.mastery_rank || 0;

    if (rank > 30) {
        rank -= 30;
        document.querySelector('.HeaderUserProfile').classList.add('is-legendary');
        document.querySelector('.HeaderUserProfile-legendary .HeaderUserProfile-masteryNumber').innerText = rank;
    } else {
        document.querySelector('.HeaderUserProfile-mastery .HeaderUserProfile-masteryNumber').innerText = rank;
    }
    // Show player avatar
    if (cookieData.avatar) {
        document.querySelector('.HeaderUserProfile-avatar').src = cookieData.avatar;
    }
    // Show player plat count in profile
    const platFields = document.querySelectorAll('.HeaderUserProfile-platinumNumber');
    platFields.forEach((field) => {
        field.innerText = (cookieData.platinum || 0).toLocaleString();
    });
    // Twitch linking
    if (cookieData.twitch_linked) {
        document.querySelector('.HeaderUserMenu-twitch').classList.add('is-linked');
    } else {
        document.querySelector('.HeaderUserMenu-twitch').classList.add('is-unlinked');
    }

    if(cookieData.cross_save) {
        document.querySelector('.HeaderUserMenu-crossSave').classList.add('is-enabled');
    } else {
        document.querySelector('.HeaderUserMenu-crossSave').remove();
    }

    let isDotShowing = false;

    // Show plat count
    document.querySelector('.HeaderUserMenu-platinumNumber').innerText = (cookieData.platinum || 0).toLocaleString();

    // Show coupon line if coupon active
    if (cookieData.coupon && cookieData.coupon.active) {

        let couponRow = null;
        if (cookieData.coupon.discount) {
            couponRow = document.querySelector('.HeaderUserMenu-couponDiscount');
            couponRow.classList.add('is-active');
            document.querySelector('.HeaderUserMenu-couponDiscountPercent').innerText = cookieData.coupon.discount;
        } else if(cookieData.coupon.multiplier) {
            couponRow = document.querySelector('.HeaderUserMenu-couponMultiplier');
            couponRow.classList.add('is-active');
            document.querySelector('.HeaderUserMenu-couponMultiplierAmount').innerText = Math.round((cookieData.coupon.multiplier-1)*100);
        }

        if (!sessionStorage.getItem(KEY_SESSIONSTORAGE_NOTIFICATION_READ_PRIME)) {
            isCouponNotificationActive = true;
            couponRow && couponRow.classList.add('is-highlighted');
            isDotShowing = true;
        }

    }
    if (isDotShowing) {
        document.querySelector('.HeaderUserProfile').classList.add('is-notification-active');
    }

    // Resync header after update
    Warframe.syncHeader && Warframe.syncHeader();
}

// Hook up analytics triggers
function parsePayload(raw) {
    const tokens = raw.split(',');
    const payload = {};
    for (const token of tokens) {
        if (token) {
            const pair = token.split(':');
            if (pair.length > 1) {
                payload[pair[0]] = pair[1];
            } else {
                payload[token] = token;
            }
        }
    }
    return payload;
}

export function initialize() {
    PageComponents.afterLoad(() => {
        // Populates UI with logged-in user data
        let _ = getUserData(setUserData);

        flashMessage();

        // Used by flash alerts. Remove when we move that to a proper component
        const AlertElement = document.querySelector('.alert');
        const flashCookie = document.cookie.match(/(?:^|;\s*)flash_data=([^;]*)/);
        if (AlertElement) {
            const Closers = AlertElement.querySelectorAll('.close,.alert-danger ul li a');
            if (Closers) {
                Closers.forEach((closer) => {
                    closer.addEventListener('click', (e) => {
                        document.activeElement && document.activeElement.blur();
                        AlertElement.classList.add('dismissed');
                        if(flashCookie) {
                            document.cookie = `flash_data=; max-age=0; path=/; domain=`+ window.location.hostname;
                        }
                    });
                })
            }
        }

        const AnalyticsElements = document.querySelectorAll("[data-analytics-event]");
        if (AnalyticsElements && AnalyticsElements.length > 0) {
            AnalyticsElements.forEach((element) => {
                const event = element.getAttribute("data-analytics-event");
                if (event) {
                    let payload = {};
                    if (element.hasAttribute("data-analytics-payload")) {
                        try {
                            payload = {...payload, ...parsePayload(element.getAttribute("data-analytics-payload"))};
                        } catch (e) {
                            console.warn("(Analytics) Could not parse payload attribute: " + e, element);
                        }
                    } else if (event !== 'click') {
                        try {
                            payload = {...payload, ...parsePayload(event)};
                        } catch (e) {
                            console.warn("(Analytics) Could not parse event attribute: " + e, element);
                        }
                    }
                    if (element.hasAttribute("data-analytics-name")) {
                        payload["event"] = element.getAttribute("data-analytics-name");
                    }
                    if (element.hasAttribute("data-analytics-type")) {
                        payload["creative_type"] = element.getAttribute("data-analytics-type");
                    }
                    if (element.hasAttribute("data-analytics-title")) {
                        payload["creative_title"] = element.getAttribute("data-analytics-title");
                    }
                    if (element.hasAttribute("data-analytics-placement")) {
                        payload["creative_placement"] = element.getAttribute("data-analytics-placement");
                    }
                    if (element.hasAttribute("data-analytics-position")) {
                        payload["creative_position"] = element.getAttribute("data-analytics-position");
                    }
                    if (element.hasAttribute("data-analytics-experiment-id")) {
                        payload["experiment_id"] = element.getAttribute("data-analytics-experiment-id");
                    }
                    if (element.hasAttribute("data-analytics-variant-id")) {
                        payload["variant_id"] = element.getAttribute("data-analytics-variant-id");
                    }
                    if (element.hasAttribute("data-analytics-pack-name")) {
                        payload["pack_name"] = element.getAttribute("data-analytics-pack-name");
                    }
                    if (element.hasAttribute("href")) {
                        const href = element.getAttribute("href");
                        if (href !== '#') {
                            payload["destination_url"] = href;
                        }
                    }

                    addDataLayerListener({
                        element,
                        event: 'click',
                        payload
                    });
                }
            })
        }

        // Automated analytics
        try {
            autowireAnalytics();
        } catch (e) {
            console.error('Error, analytics autowiring failed to execute: ' + e);
        }
    });


    PageComponents.initialize();
}

const Warframe = {
    isHeaderActive,
    isMobile: () => {
        return !tabletMatchList.matches;
    },
    isTablet: () => {
        return tabletMatchList.matches;
    },
    isDesktop: () => {
        return desktopMatchList.matches;
    },
    isWide: () => {
        return wideMatchList.matches;
    },
    elementOrParentAlreadyHasListener,
    elementParentAnchor,
    detectAnalyticsPlacement,
    addDataLayerListener,
    addDataLayerTrigger,
    openAnalyticsDebug,
    lockPageScrolling,
    unlockPageScrolling,
    isInVerticalView,
    setUserData,
    initialize,
}

if (isHeaderActive()) {
    // Header adaptive responsiveness
    const headerNavBarElem = document.querySelector(".HeaderNavigationBar");
    const headerLinkContainerElem = document.querySelector(".HeaderNavigationBar-menuContainer");
    const headerLinksElem = document.querySelector(".HeaderDesktopMenu");
    const headerControlsContainerElem = document.querySelector(".HeaderNavigationBar-controlsContainer");
    const headerControlsElem = document.querySelector(".HeaderNavigationControls");

    let syncHeaderDebounce = null;
    const headerSyncHandler = () => {
        if (syncHeaderDebounce != null) {
            clearTimeout(syncHeaderDebounce);
        }
        syncHeaderDebounce = setTimeout(syncHeader, 50);
    }
    const syncHeader = () => {
        requestAnimationFrame(() => {
            const headerState = {collapsed:false, mobile:false};
            headerNavBarElem.classList.remove('is-collapsed', 'is-mobile');
            if (headerLinksElem.offsetWidth === headerLinkContainerElem.offsetWidth) {
                headerNavBarElem.classList.add('is-collapsed');
                headerState.collapsed = true;

                if (headerControlsElem.offsetWidth > headerControlsContainerElem.offsetWidth) {
                    headerNavBarElem.classList.add('is-mobile');
                    headerState.mobile = true;
                }
            }
            try {
                sessionStorage.setItem("WF_GlobalNavState", JSON.stringify(headerState));
            } catch (e) {
                // Do nothing if session storage fails
            }
        });
    };
    window.addEventListener('resize', headerSyncHandler);
    window.addEventListener('load', syncHeader);
    Warframe.syncHeader = syncHeader;

    function clearUserNotifications() {
        if (isPrimeNotificationActive) {
            sessionStorage.setItem(KEY_SESSIONSTORAGE_NOTIFICATION_READ_PRIME, "true");
            // document.querySelector('.HeaderUserMenu-primeGaming').classList.remove('is-highlighted');
            isPrimeNotificationActive = false;
        }
        if (isCouponNotificationActive) {
            sessionStorage.setItem(KEY_SESSIONSTORAGE_NOTIFICATION_READ_COUPON, "true");
            // document.querySelector('.HeaderUserMenu-coupon').classList.remove('is-highlighted');
            isCouponNotificationActive = false;
        }
        document.querySelector('.HeaderUserProfile').classList.remove('is-notification-active');
    }

    // User menu hovers
    let hideMenuDebounce = null;

    function userMenuHoverHandler(e) {
        if (!headerNavBarElem.classList.contains('is-collapsed')) {
            e.stopPropagation();
            headerNavBarElem.classList.toggle('is-user-open', true);
            hideMenuDebounce && clearTimeout(hideMenuDebounce);
        }
    }

    document.querySelector('.HeaderUserProfile').addEventListener('mouseenter', userMenuHoverHandler);
    document.querySelector('.HeaderUserMenu').addEventListener('mouseenter', userMenuHoverHandler);

    function userMenuBlurHandler(e) {
        if (!headerNavBarElem.classList.contains('is-collapsed')) {
            e.stopPropagation();
            hideMenuDebounce && clearTimeout(hideMenuDebounce);
            hideMenuDebounce = setTimeout(() => {
                headerNavBarElem.classList.toggle('is-user-open', false);
                clearUserNotifications();
            }, 50);
        }
    }

    document.querySelector('.HeaderUserProfile').addEventListener('mouseleave', userMenuBlurHandler);
    document.querySelector('.HeaderUserMenu').addEventListener('mouseleave', userMenuBlurHandler);

    // User menu toggle
    document.querySelector('.HeaderUserProfile').addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation();
        const open = headerNavBarElem.classList.toggle('is-user-open');
        open && headerNavBarElem.classList.toggle('is-open', false);
        !open && clearUserNotifications();
    });
    // Mobile menu toggle
    document.querySelector('.HeaderNavigationBar-mobileMenuToggleInner').addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation();
        const open = headerNavBarElem.classList.toggle('is-open');
        open && headerNavBarElem.classList.toggle('is-user-open', false);
    });
    // Mobile menu overlay toggle
    document.querySelector('.HeaderNavigationBar-mobileMenuOverlay').addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation();
        if (headerNavBarElem.classList.contains('is-user-open')) {
            clearUserNotifications();
        }
        headerNavBarElem.classList.remove('is-open', 'is-user-open');
    });

    // Nav link handlers
    const navLinks = document.querySelectorAll(".HeaderDesktopMenu .HeaderMenuItem");

    // Clear locked dropdown (desktop)
    const navigationLinkClearCurrent = (resetCurrentToOriginal = false) => {
        for (let i = 0; i < navLinks.length; i++) {
            navLinks[i].classList.toggle('is-current', resetCurrentToOriginal && navLinks[i].classList.contains('is-original'));
            navLinks[i].classList.remove('is-open');
        }
    };

    // Locks underline to clicked node
    const navigationLinkClickHandler = (elem, event) => {
        navigationLinkClearCurrent();
        elem.classList.add('is-current');
    }
    // Lock open desktop nav dropdown
    const navigationDropdownClickHandler = (elem, event) => {
        event && event.preventDefault();
        event && event.stopPropagation();
        if (elem.classList.contains('is-open')) {
            const original = document.querySelector('.HeaderDesktopMenu .HeaderMenuItem.is-original');
            for (let i = 0; i < navLinks.length; i++) {
                navLinks[i].classList.remove('is-open', 'is-current', 'is-closed');
            }
            original && original.classList.add('is-current');
        } else {
            for (let i = 0; i < navLinks.length; i++) {
                const isSelected = navLinks[i] === elem;
                navLinks[i].classList.toggle('is-open', isSelected);
                navLinks[i].classList.toggle('is-current', isSelected);
                navLinks[i].classList.toggle('is-closed', !isSelected);
            }
        }
    }

    // Page nav load handler
    window.PageComponents.afterLoad(() => {
        navLinks.forEach((navLink) => {
            const dropdown = navLink.querySelector('.HeaderMenuItem-dropdown');
            if (dropdown) {
                dropdown.addEventListener("click", navigationLinkClickHandler.bind(null, navLink));
                navLink.querySelector('.HeaderMenuItem-label').addEventListener("click", navigationDropdownClickHandler.bind(null, navLink));
            }
            if (navLink.classList.contains('is-current')) {
                navLink.classList.remove('is-loading');
            }
        });

        // Nav takeover handler
        const LOCALSTORAGE_TAKEOVER_KEY = "Warframe_TakeoverClosed";
        // Change this with every new campaign
        const LOCALSTORAGE_TAKEOVER_VALUE = "WITW";
        const takeoverElem = document.getElementById("header-takeover");

        function forceStickyNavPosition() {
            document.documentElement.scrollTop += 1;
            document.documentElement.scrollTop -= 1;
        }

        if (takeoverElem && localStorage.getItem(LOCALSTORAGE_TAKEOVER_KEY) !== LOCALSTORAGE_TAKEOVER_VALUE) {
            takeoverElem.classList.add('is-primed');

            function dismissTakeover() {
                localStorage.setItem(LOCALSTORAGE_TAKEOVER_KEY, LOCALSTORAGE_TAKEOVER_VALUE);
                takeoverElem.classList.remove('is-active');
                setTimeout(() => {
                    takeoverElem.classList.remove('is-primed');
                    forceStickyNavPosition();
                }, 1000);

            }

            // Disable takeover if any link is clicked
            takeoverElem.querySelectorAll('.HeaderTakeover-link').forEach(
                (link) => {
                    link.addEventListener('click', dismissTakeover)
                }
            )
            const takeoverCloser = takeoverElem.querySelector('.HeaderTakeover-closer');
            if (takeoverCloser) {
                takeoverCloser.addEventListener('click', () => {
                    dismissTakeover();
                });
            }
            setTimeout(() => {
                    takeoverElem.classList.add('is-active');

                    setTimeout(() => {
                        forceStickyNavPosition();
                    }, 1005);
                }, 1000);
        }
    });
}

// Footer language dropdown handlers
const footerLanguageDropdown = document.getElementById('footer-language-dropdown');
if (footerLanguageDropdown) {
    footerLanguageDropdown.addEventListener('click', (e) => {
        e.stopPropagation();
        footerLanguageDropdown.classList.toggle('is-open');
    });

    document.addEventListener('click', (e) => {
        footerLanguageDropdown.classList.remove('is-open');
    });
}

const footerNavBarElem = document.querySelector(".FooterNavigationBar");
if (footerNavBarElem) {
    const footerLinksElem = footerNavBarElem.querySelector(".FooterNavigationBar-links");
    const footerDropdownContainerElem = document.querySelector(".FooterNavigationBar-dropdownContainer");

    let syncFooterDebounce = null;
    const footerLinkBarSyncHandler = () => {
        if (syncFooterDebounce != null) {
            clearTimeout(syncFooterDebounce);
        }
        syncFooterDebounce = setTimeout(syncFooterLinkBar, 50);
    }
    const syncFooterLinkBar = () => {
        requestAnimationFrame(() => {
            if (Warframe.isTablet()) {
                footerNavBarElem.classList.add('is-expanded');
                if (footerLinksElem.getBoundingClientRect().right > footerDropdownContainerElem.getBoundingClientRect().left) {
                    footerNavBarElem.classList.remove('is-expanded');
                }
            } else {
                footerNavBarElem.classList.toggle('is-expanded', false);
            }
        });
    };
    window.addEventListener('resize', footerLinkBarSyncHandler);
    window.addEventListener('load', syncFooterLinkBar);
}

// Show time-locked nav elements
const timelocked = document.querySelector('.HeaderMenuItem-live.is-hidden');
const now = Date.now();
if (timelocked) {
    if (timelocked.getAttribute('data-show-override') === "1" || (
        timelocked.getAttribute('data-show-from') <= now
        && now <= timelocked.getAttribute('data-show-until')
    )) {
        timelocked.classList.remove('is-hidden');
    }
}

// Images with fallbacks
const pageImages = document.querySelectorAll('img');
pageImages && pageImages.forEach((image) => {
    if (image.dataset.fallback) {
        image.onerror = (e) => {
            image.onerror = null;
            image.src = image.dataset.fallback;
        }
    }
});

// Legacy methods used by external GTM scripts
window.getCookie = (cookieName) => {
    const name = cookieName + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const tokens = decodedCookie.split(';');
    for (let i = 0; i < tokens.length; i++) {
        let c = tokens[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
}
window.setCookie = (cookieName, cookieValue, expirationDays) => {
    const date = new Date();
    date.setTime(date.getTime() + (expirationDays * 24 * 60 * 60 * 1000));
    let expires = "expires="+date.toUTCString();
    document.cookie = cookieName + "=" + cookieValue + ";" + expires + ";path=/";
}

// ... and joining old `Warframe` and new `window.Warframe`
window.Warframe = { ... window.Warframe, ... Warframe};

export default Warframe;
